import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type {
  SubscriptionCenterSubscriptionTopicRequest,
  SubscriptionCenterUpdateSubscriptionTopicRequest,
  SubscriptionTopic as SubscriptionTopicModel,
} from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Subscription topic — a content category the audience can subscribe to or
 * unsubscribe from (`/subscription_topics`).
 *
 * **`id` is `string` in the entity.** The wire encodes it as a number
 * (the generated `SubscriptionTopic.id: number`) but every other entity
 * in this layer uses string ids and `Campaign.subscription_topic_id` is
 * `string` on the wire too, so we coerce at the parse boundary
 * (number → string) for one consistent type across consumers. The reorder
 * endpoint takes numeric ids, so its caller converts back.
 */

export type SubscriptionTopic = Omit<SubscriptionTopicModel, 'id' | 'created' | 'updated'> & {
  id: string;
  created: Date;
  updated: Date;
};

const SubscriptionTopicFromApi = readSchema<SubscriptionTopic>(
  z.looseObject({
    id: z.number().transform(String),
    created: epochSecondsToDate,
    updated: epochSecondsToDate,
  }),
);

/**
 * The create body fields, derived from the generated create request.
 *
 * `id` is dropped: the create handler assigns it and the update handler reads
 * it from the path, so neither reads the body's copy.
 */
export type SubscriptionTopicWriteInput = Omit<SubscriptionCenterSubscriptionTopicRequest, 'id'>;

/**
 * The update body fields, derived from the generated update request, which
 * publishes `opt_in` as required: the update handler overwrites the stored
 * value with whatever the body carries, so an omitted flag would silently
 * unsubscribe the topic. Every caller states it.
 */
export type UpdateSubscriptionTopicWriteInput = Omit<SubscriptionCenterUpdateSubscriptionTopicRequest, 'id'>;

/** @public */
export const subscriptionTopic = defineEntity<
  SubscriptionTopic,
  SubscriptionTopicWriteInput,
  void,
  undefined,
  'workspace'
>({
  name: 'subscription_topic',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SubscriptionTopicFromApi },
});

export const subscriptionTopicKeys = subscriptionTopic.keys;

export function parseSubscriptionTopic(raw: SubscriptionTopicModel): SubscriptionTopic {
  return subscriptionTopic.parse(raw);
}

/**
 * Complete the create fields into the topic request the create sends.
 *
 * `opt_in` is optional on the create contract because the handler defaults it
 * to false; sending that same default keeps the request explicit about the
 * value the new topic starts from. The update path needs no completion — its
 * request type requires the flag, so callers supply it.
 */
export function toCreateSubscriptionTopicRequest(
  input: SubscriptionTopicWriteInput,
): SubscriptionCenterSubscriptionTopicRequest {
  return { ...input, opt_in: input.opt_in ?? false };
}
